import * as React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
  Button,
  Spinner,
  Text,
  Badge,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { Event } from '../../../types/event';
import { getSeverityBadgeColor, getSeverityText } from '../../../utils/severityHelpers';

const useStyles = makeStyles({
  container: {
    width: '100%',
  },
  table: {
    // Tabela visível apenas em desktop
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileCards: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  card: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardDescricao: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  cardDetalhe: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  // Estilos para controlar largura das colunas
  descricaoCell: {
    width: '25%',
    minWidth: '150px',
  },
  detalheCell: {
    width: '30%',
    minWidth: '180px',
  },
  severidadeCell: {
    width: '10%',
    minWidth: '80px',
  },
  tipoCell: {
    width: '10%',
    minWidth: '80px',
  },
  consultaERPCell: {
    width: '10%',
    minWidth: '100px',
    textAlign: 'center',
  },
  marcadorCell: {
    width: '15%',
    minWidth: '120px',
  },
  estruturasCell: {
    width: '10%',
    minWidth: '80px',
    textAlign: 'center',
  },
  actionsHeaderCell: {
    width: '10%',
    minWidth: '120px',
  },
  // Estilos para truncar texto longo
  detalheText: {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
});

interface EventGridProps {
  events: Event[];
  onEdit: (event: Event) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  error?: string | null;
}

const EventGrid: React.FC<EventGridProps> = ({
  events,
  onEdit,
  onDelete,
  loading,
  error,
}) => {
  const styles = useStyles();

  const getSeverityColor = (severity: number) => {
    return getSeverityBadgeColor(severity as 0 | 1 | 2 | 3);
  };

  const getSeverityLabel = (severity: number) => {
    return getSeverityText(severity as 0 | 1 | 2 | 3);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Carregando eventos..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text>Erro ao carregar eventos: {error}</Text>
      </div>
    );
  }

  const columns = [
    { key: 'descricao', label: 'Descrição' },
    { key: 'detalhe', label: 'Detalhe' },
    { key: 'severidade', label: 'Severidade' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'consultaERPId', label: 'Consulta ERP ID' },
    { key: 'descricaoMarcador', label: 'Marcador' },
    { key: 'estruturas', label: 'Estruturas' },
    { key: 'acoes', label: 'Ações' },
  ];

  // Função helper para mapear chave da coluna para classe CSS
  const getColumnClass = (key: string) => {
    switch (key) {
      case 'descricao': return styles.descricaoCell;
      case 'detalhe': return styles.detalheCell;
      case 'severidade': return styles.severidadeCell;
      case 'tipo': return styles.tipoCell;
      case 'consultaERPId': return styles.consultaERPCell;
      case 'descricaoMarcador': return styles.marcadorCell;
      case 'estruturas': return styles.estruturasCell;
      case 'acoes': return styles.actionsHeaderCell;
      default: return '';
    }
  };

  // Função helper para renderizar conteúdo da célula
  const renderCellContent = (event: Event, key: string) => {
    switch (key) {
      case 'descricao':
        return event.descricao;
      case 'detalhe':
        return (
          <Text className={styles.detalheText}>
            {event.detalhe}
          </Text>
        );
      case 'severidade':
        return (
          <Badge
            appearance="filled"
            color={getSeverityColor(event.severidade)}
          >
            {getSeverityLabel(event.severidade)}
          </Badge>
        );
      case 'tipo':
        return event.tipo === 1 ? 'Livre' : 'Diagnóstico';
      case 'consultaERPId':
        return (
          <Text style={{ textAlign: 'center', fontWeight: 'semibold' }}>
            {event.consultaERPId || '-'}
          </Text>
        );
      case 'descricaoMarcador':
        return event.descricaoMarcador;
      case 'estruturas':
        const estruturasCount = event.estruturaEventos?.length || 0;
        return (
          <Text style={{ textAlign: 'center', fontWeight: 'semibold' }}>
            {estruturasCount}
          </Text>
        );
      case 'acoes':
        return (
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<Edit20Regular />}
              onClick={() => onEdit(event)}
              size="small"
            >
              Editar
            </Button>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => onDelete(event.id)}
              size="small"
            >
              Excluir
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Tabela para desktop */}
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={getColumnClass(column.key)}>
                {column.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id}>
              {columns.map((column) => (
                <TableCell key={column.key} className={getColumnClass(column.key)}>
                  {renderCellContent(event, column.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Cards para mobile */}
      <div className={styles.mobileCards}>
        {events.map((event) => (
          <div key={event.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardContent}>
                <Text className={styles.cardDescricao}>{event.descricao}</Text>
                <Text className={styles.cardDetalhe}>{event.detalhe}</Text>
              </div>
              <Badge
                appearance="filled"
                color={getSeverityColor(event.severidade)}
              >
                {getSeverityLabel(event.severidade)}
              </Badge>
            </div>
            <div className={styles.cardFooter}>
              <div>
                <Text>Tipo: {event.tipo === 1 ? 'Livre' : 'Diagnóstico'}</Text>
                <Text>Consulta ERP ID: {event.consultaERPId || '-'}</Text>
                <Text>Marcador: {event.descricaoMarcador}</Text>
                <Text>Estruturas: {event.estruturaEventos?.length || 0}</Text>
              </div>
              <div className={styles.cardActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  onClick={() => onEdit(event)}
                  size="small"
                >
                  Editar
                </Button>
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => onDelete(event.id)}
                  size="small"
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventGrid;